import type { GscConnectFailureReason } from '../SearchPerformanceAnalyticsEvents';

// A failed connection attempt, held in wizard state so it renders as a state
// with a next step instead of a toast that vanishes.
export type GscConnectionError = {
  code: GscConnectFailureReason;
  // Raw upstream text, carried for analytics only — never rendered. It is either
  // a visibility enum name or a Google error slug, neither of which means
  // anything to a customer.
  detail?: string;
};

export type GscConnectionErrorCopy = {
  message: string;
  description: string;
};

// Visibility reports a failed OAuth leg by redirecting to
// `?status=error&reason=<UPPERCASE_CODE>` and sends no machine-readable
// `error_code` alongside it (prerender-core:
// OAuthOrchestratorService.buildCallbackErrorUrl). The reason *is* the code, so
// it has to be translated here — rendering it verbatim showed customers raw
// enum names like TOKEN_EXCHANGE_FAILED, and reading the absent `error_code`
// bucketed every visibility failure as `oauth_error` in analytics.
//
// Several upstream codes deliberately collapse into one: they differ for us and
// not for the customer, whose next step is identical.
const VISIBILITY_REASON_CODES: Record<string, GscConnectFailureReason> = {
  ACCESS_DENIED: 'access_denied',
  TOKEN_EXCHANGE_FAILED: 'token_exchange_failed',
  TOKEN_REFRESH_FAILED: 'token_exchange_failed',
  TOKEN_REVOKED: 'token_revoked',
  // "Start over" for all three: the link was replayed, expired, tampered with,
  // or its source disappeared.
  INVALID_STATE: 'invalid_state',
  SOURCE_NOT_FOUND: 'invalid_state',
  INVALID_PROPERTY_SELECTION: 'invalid_state',
  PROPERTY_ACCESS_DENIED: 'property_access_denied',
  ENCRYPTION_FAILED: 'upstream_error',
  API_ERROR: 'upstream_error',
};

const GSC_CONNECTION_ERROR_COPY: Record<
  GscConnectFailureReason,
  GscConnectionErrorCopy
> = {
  access_denied: {
    message: 'Google sign-in was cancelled',
    description:
      'Nothing was connected. Prerender needs read-only access to your Search Console data — start again and choose Allow to finish connecting.',
  },
  scope_denied: {
    message: "Google didn't grant all the access we need",
    description:
      'Prerender needs read-only Search Console access and your Google account email, so we can show which account connected. Start again and leave both permissions selected.',
  },
  popup_closed: {
    message: 'The Google sign-in window closed before it finished',
    description:
      'Nothing was connected. Start again and keep the window open until it returns to Prerender.',
  },
  google_error: {
    message: "Google couldn't complete the sign-in",
    description:
      'This is usually temporary. Try again in a moment, or use a different Google account.',
  },
  relay_unavailable: {
    message: 'The Search Console connection is unavailable here',
    description:
      "This environment isn't configured for Google Search Console. Contact support if you are seeing this in production.",
  },
  token_exchange_failed: {
    message: "Google didn't confirm the sign-in",
    description:
      'The sign-in expired before we could complete it. Start the connection again — it usually succeeds on the second attempt.',
  },
  token_revoked: {
    message: "Prerender's access to your Google account was revoked",
    description:
      'Reconnect to restore access. You can review which apps have access in your Google account settings, under third-party apps.',
  },
  invalid_state: {
    message: 'This connection link is no longer valid',
    description:
      'Connection links expire after a few minutes and work only once. Start the connection again from this page.',
  },
  property_access_denied: {
    message: "Your Google account can't read this property",
    description:
      'Ask a Search Console owner to give your Google account Owner or Full access to the property, then connect again.',
  },
  upstream_error: {
    message: "Google Search Console didn't respond as expected",
    description: 'This is usually temporary. Try again in a few minutes.',
  },
  request_failed: {
    message: "Couldn't start the Google connection",
    description: 'Something went wrong on our side. Try again in a moment.',
  },
  stuck_source: {
    message: "A previous connection attempt didn't finish",
    description: "We've cleared it up. Start the connection again.",
  },
  oauth_error: {
    message: "The Google connection didn't complete",
    description: 'No changes were made to your account. Start the connection again.',
  },
  malformed_result: {
    message: "The Google connection didn't complete",
    description: 'No changes were made to your account. Start the connection again.',
  },
};

export function describeGscConnectionError(
  code: GscConnectFailureReason,
): GscConnectionErrorCopy {
  return GSC_CONNECTION_ERROR_COPY[code] ?? GSC_CONNECTION_ERROR_COPY.oauth_error;
}

// `errorCode` is set by our own callback route (a code we chose); `reason` comes
// from visibility and needs the table above. Anything unrecognised degrades to
// `oauth_error` rather than polluting the union.
export function toGscConnectionErrorCode(
  errorCode: string | undefined,
  reason: string | undefined
): GscConnectFailureReason {
  // hasOwnProperty rather than `in`: the code arrives from a URL param, and
  // `in` also matches inherited keys like `toString`.
  if (errorCode && Object.prototype.hasOwnProperty.call(GSC_CONNECTION_ERROR_COPY, errorCode)) {
    return errorCode as GscConnectFailureReason;
  }
  if (reason) {
    const mapped = VISIBILITY_REASON_CODES[reason.trim().toUpperCase()];
    if (mapped) {
      return mapped;
    }
  }
  return 'oauth_error';
}
